import { useLazyQuery, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button } from 'reactstrap';
import GoBack from '../../../components/GoBack';
import InputField from '../../../components/layout/InputField';
import Loader from '../../../components/layout/Loader';
import NoDataFound from '../../../components/layout/NoDataFound';
import Pagination from '../../../components/layout/Pagination';
import TableComponent from '../../../components/layout/TableComponent';
import { BILLING_CREDITS_TABLE_HEADER, BILLING_CREDITS_TABLE_HEADER_ADV_SEARCH } from '../../../constants';
import { BILLING_CREDITS_ADMIN_ROUTE, PAGE_LIMIT, SETTINGS_ROUTE } from '../../../utils/constant';
import EDIT_ICON from '../../../assets/images/edit-icon-menu.svg';
import DELETE_ICON from '../../../assets/images/delete-icon-menu.svg';
import BillingCreditModal from './BillingCreditModal';
import { DELETE_BILLING_CREDIT, GET_ALL_BILLING_CREDITS } from './gql';

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const defaultColumnFilters = {
  userSearch: '',
  billingTypeSearch: '',
  monthSearch: '',
  yearSearch: '',
};

const BillingCredits = ({ location, history }: any): JSX.Element => {
  const page = new URLSearchParams(location.search).get('currentPage') || '1';
  const [currentPage, setCurrentPage] = useState(parseInt(page));
  const [limit, setLimit] = useState<number>(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<any>(null);
  const [columnFilters, setColumnFilters] = useState(defaultColumnFilters);

  const { register, setValue } = useForm();

  const [fetchBillingCredits, { loading, data, error }] = useLazyQuery(GET_ALL_BILLING_CREDITS, {
    fetchPolicy: 'network-only',
  });

  const [deleteBillingCredit] = useMutation(DELETE_BILLING_CREDIT);

  const load = (page = currentPage, filters = columnFilters) => {
    history.push(`${BILLING_CREDITS_ADMIN_ROUTE}?currentPage=${page}`);
    fetchBillingCredits({
      variables: {
        page,
        limit,
        filters: {
          userSearch: filters.userSearch || undefined,
          billingTypeSearch: filters.billingTypeSearch || undefined,
          monthSearch: filters.monthSearch || undefined,
          yearSearch: filters.yearSearch || undefined,
        },
      },
    });
  };

  useEffect(() => {
    load(currentPage, columnFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit, columnFilters]);

  const changeFiled = (fieldName: string, value: any) => {
    setCurrentPage(1);
    setColumnFilters((prev) => ({ ...prev, [fieldName]: value }));
  };

  const openAdd = () => {
    setEditRecord(null);
    setModalOpen(true);
  };

  const openEdit = (record: any) => {
    setEditRecord(record);
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this billing credit?')) return;
    try {
      await deleteBillingCredit({ variables: { id } });
      toast.success('Billing credit deleted.');
      load();
    } catch {
      toast.error('Failed to delete billing credit.');
    }
  };

  const records = data?.getAllBillingCredits?.items || [];
  const count = data?.getAllBillingCredits?.count || 0;

  return (
    <div>
      <GoBack url={SETTINGS_ROUTE} />

      <div className="priamryHeading primaryHeaderSpacing d-flex justify-content-between align-items-center">
        <h5 className="m-0">Billing Credits</h5>
        <Button color="primary" onClick={openAdd}>Add Billing Credit</Button>
      </div>

      <div className="table-responsive billing-credits-table">
        <TableComponent
          tableHeader={BILLING_CREDITS_TABLE_HEADER}
          advanceSearching
          tableHeaderAdvSeacrh={BILLING_CREDITS_TABLE_HEADER_ADV_SEARCH}
          handleChange={changeFiled}
          columnFilters={columnFilters}
          register={register}
          setValue={setValue}
          tableName="BillingCredits"
        >
          {error && !records.length && (
            <tbody>
              <tr>
                <td colSpan={6} className="empty-table-td">
                  <p className="text-danger text-center mb-0">Failed to load billing credits. Please refresh.</p>
                </td>
              </tr>
            </tbody>
          )}

          {loading && (
            <tbody>
              <tr>
                <td colSpan={6}>
                  <Loader loaderClass="sm-height" />
                </td>
              </tr>
            </tbody>
          )}

          {!loading && !records.length && (
            <tbody>
              <tr>
                <td colSpan={6} className="empty-table-td">
                  <NoDataFound text="No billing credits found." />
                </td>
              </tr>
            </tbody>
          )}

          {!loading && !!records.length && (
            <tbody>
              {records.map((record: any) => (
                <tr key={record.id}>
                  <td>{record?.user?.fullName || '—'}</td>
                  <td style={{ textTransform: 'capitalize' }}>{record.billingType}</td>
                  <td>${record.billingCreditAmount?.toLocaleString()}</td>
                  <td>{MONTH_NAMES[record.currentMonth]}</td>
                  <td>{record.currentYear}</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <img
                        src={EDIT_ICON}
                        alt="edit"
                        width="20px"
                        className="mr-2"
                        style={{ cursor: 'pointer' }}
                        onClick={() => openEdit(record)}
                      />
                      <img
                        src={DELETE_ICON}
                        alt="delete"
                        width="20px"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleDelete(record.id)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </TableComponent>
      </div>

      {!loading && count > 0 && (
        <div className="users-pagination">
          <Pagination
            onPageChange={(pageClicked: number) => {
              setCurrentPage(pageClicked);
              history.push(`${BILLING_CREDITS_ADMIN_ROUTE}?currentPage=${pageClicked}`);
            }}
            pageCount={Math.ceil(count / limit)}
            currentPage={currentPage}
          />

          <div className="d-flex align-items-center">
            <div className="pagination-display paginationItemsSelect">
              <p className="m-0">
                {(currentPage - 1) * limit + 1} to{' '}
                {Math.min(currentPage * limit, count)} of {count}
              </p>
              <div className="divider"></div>
              <label>Display</label>
              <InputField
                label=""
                inputtype="select"
                selectItems={PAGE_LIMIT.filter((item) => item.value !== limit)}
                inputid="limit"
                placeholder={limit.toString()}
                inputMethod={(value: string) => {
                  setCurrentPage(1);
                  setLimit(parseInt(value));
                }}
              />
            </div>
          </div>
        </div>
      )}

      <BillingCreditModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        editRecord={editRecord}
        onSuccess={() => load()}
      />
    </div>
  );
};

export default withRouter(BillingCredits);
